<template>
  <div class="page-container">
    <div class="info-bar">
      首次可通过“拉取MT5基线”同步 `mt5_holidays`；后续新增或编辑会先保存业务配置，再自动同步到 MT5。
    </div>

    <div class="panel-card search-card">
      <el-form :model="searchForm" label-position="top" class="search-form">
        <div class="search-fields">
          <el-form-item label="节假日名称" class="field-item field-name">
            <el-input v-model="searchForm.description" placeholder="节假日名称" clearable class="field-control" />
          </el-form-item>
          <el-form-item label="交易品种" class="field-item field-product">
            <el-select
              v-model="searchForm.symbolScope"
              placeholder="全部"
              clearable
              filterable
              class="field-control"
            >
              <el-option
                v-for="item in productOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="状态" class="field-item field-status">
            <el-select v-model="searchForm.status" placeholder="全部" clearable class="field-control">
              <el-option label="全部" :value="null" />
              <el-option label="启用" :value="1" />
              <el-option label="禁用" :value="2" />
            </el-select>
          </el-form-item>
          <el-form-item label="日期范围" class="field-item field-date">
            <div class="date-range-field">
              <el-date-picker
                v-model="searchForm.startDate"
                type="date"
                value-format="yyyy-MM-dd"
                placeholder="年/月/日"
                class="date-control"
              />
              <span class="range-separator">至</span>
              <el-date-picker
                v-model="searchForm.endDate"
                type="date"
                value-format="yyyy-MM-dd"
                placeholder="年/月/日"
                class="date-control"
              />
            </div>
          </el-form-item>
        </div>
        <div class="search-actions">
          <el-button @click="resetSearch">重置</el-button>
          <el-button type="primary" icon="el-icon-search" @click="fetchList">搜索</el-button>
        </div>
      </el-form>
    </div>

    <div class="panel-card table-card">
      <div class="table-header">
        <div class="table-title">假期配置列表（共 {{ total }} 条）</div>
        <div class="header-actions">
          <el-button v-auth="'business:tradeHoliday:save'" type="primary" size="small" icon="el-icon-plus" @click="openAdd">新增假期配置</el-button>
          <el-button v-auth="'business:tradeHoliday:sync'" size="small" icon="el-icon-refresh" @click="handleSyncAll">拉取MT5基线</el-button>
          <el-button v-auth="'business:tradeHoliday:log'" size="small" icon="el-icon-document" @click="openAllLogs">业务日志</el-button>
        </div>
      </div>

      <el-table :data="tableData" border v-loading="loading" class="holiday-table">
        <el-table-column label="#" width="60" align="center">
          <template slot-scope="scope">
            {{ (pageNum - 1) * pageSize + scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column label="日期" width="170" align="center">
          <template slot-scope="scope">
            {{ formatHolidayDate(scope.row.holidayDate) }}
          </template>
        </el-table-column>
        <el-table-column label="时间(UTC)" width="150" align="center">
          <template slot-scope="scope">
            {{ formatMinuteRange(scope.row.fromTime, scope.row.toTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="description" label="节假日名称" min-width="220" show-overflow-tooltip />
        <el-table-column label="交易品种" min-width="320">
          <template slot-scope="scope">
            <div class="symbol-tag-list">
              <span
                v-for="item in splitSymbolScope(scope.row.symbolScope)"
                :key="item"
                class="scope-tag"
                :class="symbolTagClass(item)"
              >
                {{ item }}
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-switch v-auth="'business:tradeHoliday:status'" :value="scope.row.status === 1" @change="toggleStatus(scope.row)" />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" align="center">
          <template slot-scope="scope">
            <el-button v-auth="'business:tradeHoliday:edit'" type="text" size="small" class="action-text is-edit" @click="openEdit(scope.row)">编辑</el-button>
            <el-button v-auth="'business:tradeHoliday:log'" type="text" size="small" class="action-text is-log" @click="openLog(scope.row)">日志</el-button>
            <el-button
              v-auth="'business:tradeHoliday:sync'"
              v-if="scope.row.createStatus === 3"
              type="text"
              size="small"
              class="action-text is-retry"
              @click="handleRetry(scope.row)"
            >
              重试同步
            </el-button>
            <el-button v-auth="'business:tradeHoliday:delete'" type="text" size="small" class="action-text is-delete" @click="deleteRow(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="page-footer"
        @size-change="(v) => { pageSize = v; fetchList() }"
        @current-change="(v) => { pageNum = v; fetchList() }"
        :current-page="pageNum"
        :page-sizes="[10, 15, 20, 50]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      />
    </div>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="720px"
      :close-on-click-modal="false"
      class="holiday-dialog"
    >
      <el-form :model="form" label-position="top" class="holiday-form" size="small">
        <div class="dialog-section">
          <div class="section-title">基础信息</div>
          <el-row :gutter="18">
            <el-col :span="14">
              <el-form-item label="节假日名称" required>
                <el-input v-model="form.description" placeholder="如：2025 XMAS(XAUUSD)1" maxlength="255" />
              </el-form-item>
            </el-col>
            <el-col :span="10">
              <el-form-item label="日期" required>
                <el-date-picker
                  v-model="form.holidayDate"
                  type="date"
                  value-format="yyyy-MM-dd"
                  placeholder="年/月/日"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="时间范围(UTC)" required>
            <div class="time-range-row">
              <el-time-picker
                v-model="form.fromTimeText"
                value-format="HH:mm"
                format="HH:mm"
                placeholder="00:00"
                class="time-control"
              />
              <span class="range-separator">-</span>
              <el-time-picker
                v-model="form.toTimeText"
                value-format="HH:mm"
                format="HH:mm"
                placeholder="23:59"
                class="time-control"
              />
            </div>
          </el-form-item>
        </div>

        <div class="dialog-section">
          <div class="section-title">交易范围</div>
          <el-form-item label="交易品种" required>
            <el-select
              v-model="form.selectedProducts"
              multiple
              filterable
              collapse-tags
              default-first-option
              placeholder="请选择交易品种"
              style="width: 100%;"
              @change="handleProductChange"
            >
              <el-option
                v-for="item in productOptionsWithAll"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
            <div class="selected-product-preview" v-if="form.selectedProducts && form.selectedProducts.length">
              <span
                v-for="item in form.selectedProducts"
                :key="item"
                class="scope-tag preview-tag"
                :class="symbolTagClass(item)"
              >
                {{ item === '*' ? '全部产品 (*)' : item }}
              </span>
            </div>
          </el-form-item>
          <el-row :gutter="18">
            <el-col :span="12">
              <el-form-item label="模式">
                <el-select v-model="form.mode" style="width: 100%;">
                  <el-option label="默认模式" :value="1" />
                  <el-option label="其它模式" :value="2" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="状态">
                <el-switch v-model="form.status" :active-value="1" :inactive-value="2" />
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="备注">
            <el-input v-model="form.remark" type="textarea" :rows="3" placeholder="可选备注" />
          </el-form-item>
          <el-form-item label="MT5结果" v-if="form.id">
            <el-input v-model="form.mt5Retmsg" type="textarea" :rows="2" disabled />
          </el-form-item>
        </div>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="save">确认保存</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="logDialogTitle"
      :visible.sync="logDialogVisible"
      width="900px"
      :close-on-click-modal="false"
    >
      <div class="log-filter-bar">
        <span class="log-title">{{ currentLogHoliday.id ? `${currentLogHoliday.description || '-'} / ${currentLogHoliday.holidayDate || '-'}` : '全部假期日志' }}</span>
        <el-select v-model="logFilter.actionType" placeholder="全部操作类型" clearable size="small" style="width: 140px;">
          <el-option label="新增配置" :value="1" />
          <el-option label="编辑配置" :value="2" />
          <el-option label="启用假期" :value="3" />
          <el-option label="禁用假期" :value="4" />
          <el-option label="发起创建" :value="5" />
          <el-option label="创建成功" :value="6" />
          <el-option label="创建失败" :value="7" />
          <el-option label="删除配置" :value="8" />
        </el-select>
        <el-input v-model="logFilter.operator" placeholder="操作人" clearable size="small" style="width: 120px;" />
        <el-date-picker v-model="logFilter.startDate" type="date" value-format="yyyy-MM-dd" placeholder="开始日期" size="small" style="width: 140px;" />
        <el-date-picker v-model="logFilter.endDate" type="date" value-format="yyyy-MM-dd" placeholder="结束日期" size="small" style="width: 140px;" />
        <el-button size="small" @click="resetLogFilter">重置</el-button>
        <el-button type="primary" size="small" @click="fetchLogs">搜索</el-button>
      </div>

      <el-table :data="logTableData" border v-loading="logLoading" size="small" style="width: 100%;">
        <el-table-column prop="operateTime" label="操作时间" width="170" />
        <el-table-column v-if="!currentLogHoliday.id" prop="holidayDate" label="日期" width="120" align="center" />
        <el-table-column v-if="!currentLogHoliday.id" prop="description" label="节假日名称" min-width="180" show-overflow-tooltip />
        <el-table-column label="操作类型" width="120" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="actionTypeTag(scope.row.actionType)">
              {{ scope.row.actionTypeName || actionTypeText(scope.row.actionType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="operator" label="操作人" width="120" />
        <el-table-column prop="actionDetail" label="变更内容" min-width="360" show-overflow-tooltip />
      </el-table>

      <el-pagination
        @size-change="(v) => { logPageSize = v; fetchLogs() }"
        @current-change="(v) => { logPageNum = v; fetchLogs() }"
        :current-page="logPageNum"
        :page-sizes="[10, 20, 50]"
        :page-size="logPageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="logTotal"
      />
    </el-dialog>
  </div>
</template>

<script>
import {
  tradeHolidayAddOrUpdate,
  tradeHolidayDeleteBatchIds,
  tradeHolidayDetail,
  tradeHolidayList,
  tradeHolidayLogList,
  tradeHolidayRetryCreate,
  tradeHolidayStatus,
  tradeHolidaySyncAll
} from '@/api/tradeHoliday'
import { exchangeProductList } from '@/api/tradeProduct'

export default {
  name: 'TradeHolidayList',
  data() {
    return {
      loading: false,
      tableData: [],
      total: 0,
      pageNum: 1,
      pageSize: 15,
      productOptions: [],
      searchForm: this.createEmptySearchForm(),
      dialogVisible: false,
      dialogTitle: '新增假期配置',
      form: this.createEmptyForm(),
      logDialogVisible: false,
      logDialogTitle: '假期业务日志',
      logLoading: false,
      logTableData: [],
      logTotal: 0,
      logPageNum: 1,
      logPageSize: 10,
      currentLogHoliday: {
        id: null,
        description: '',
        holidayDate: ''
      },
      logFilter: this.createEmptyLogFilter()
    }
  },
  mounted() {
    this.fetchProductOptions()
    this.fetchList()
  },
  methods: {
    createEmptySearchForm() {
      return {
        description: '',
        symbolScope: '',
        status: null,
        createStatus: null,
        startDate: '',
        endDate: ''
      }
    },
    createEmptyForm() {
      return {
        id: null,
        holidayDate: '',
        description: '',
        fromTime: null,
        toTime: null,
        fromTimeText: '00:00',
        toTimeText: '23:59',
        selectedProducts: ['*'],
        symbolScope: '*',
        mode: 1,
        status: 1,
        remark: '',
        mt5Retmsg: ''
      }
    },
    createEmptyLogFilter() {
      return {
        actionType: null,
        operator: '',
        startDate: '',
        endDate: ''
      }
    },
    fetchProductOptions() {
      exchangeProductList({
        pageNum: 1,
        pageSize: 1000
      }).then(res => {
        if (res.code !== 0) return
        const list = (res.result && res.result.list) || []
        const map = new Map()
        list.forEach(item => {
          const value = this.productScopeValue(item)
          if (!value || map.has(value)) return
          map.set(value, {
            value,
            label: this.productScopeLabel(item, value)
          })
        })
        this.productOptions = Array.from(map.values())
      })
    },
    fetchList() {
      this.loading = true
      tradeHolidayList({
        pageNum: this.pageNum,
        pageSize: this.pageSize,
        ...this.searchForm
      }).then(res => {
        if (res.code === 0) {
          this.tableData = (res.result && res.result.list) || []
          this.total = (res.result && res.result.total) || 0
        }
      }).finally(() => {
        this.loading = false
      })
    },
    resetSearch() {
      this.searchForm = this.createEmptySearchForm()
      this.pageNum = 1
      this.fetchList()
    },
    handleSyncAll() {
      this.$confirm('确定从 MT5 拉取假期基线到本地吗？', '提示', { type: 'warning' }).then(() => {
        tradeHolidaySyncAll({
          ...this.searchForm
        }).then(res => {
          if (res.code === 0) {
            this.$message.success(res.result || '同步成功')
            this.fetchList()
          }
        })
      }).catch(() => {})
    },
    openAdd() {
      this.dialogTitle = '新增假期配置'
      this.form = this.createEmptyForm()
      this.dialogVisible = true
    },
    openEdit(row) {
      tradeHolidayDetail(row.id).then(res => {
        if (res.code !== 0) return
        const data = res.result || {}
        this.form = Object.assign(this.createEmptyForm(), data, {
          fromTimeText: this.intTimeToText(data.fromTime),
          toTimeText: this.intTimeToText(data.toTime),
          selectedProducts: this.parseSymbolScopeToSelection(data.symbolScope)
        })
        this.dialogTitle = `编辑假期配置：${this.form.description || ''}`
        this.dialogVisible = true
      })
    },
    handleProductChange(values) {
      if (!Array.isArray(values)) {
        this.form.selectedProducts = ['*']
        return
      }
      if (values.includes('*') && values.length > 1) {
        const latest = values[values.length - 1]
        this.form.selectedProducts = latest === '*' ? ['*'] : values.filter(item => item !== '*')
      }
    },
    save() {
      if (!this.form.description) {
        this.$message.warning('请输入节假日名称')
        return
      }
      if (!this.form.holidayDate) {
        this.$message.warning('请选择日期')
        return
      }
      if (!this.form.fromTimeText || !this.form.toTimeText) {
        this.$message.warning('请选择时间范围')
        return
      }
      if (!this.form.selectedProducts || this.form.selectedProducts.length === 0) {
        this.$message.warning('请选择交易品种')
        return
      }
      const symbolScope = this.buildSymbolScope(this.form.selectedProducts)
      const payload = {
        id: this.form.id,
        holidayDate: this.form.holidayDate,
        description: this.form.description,
        fromTime: this.textTimeToInt(this.form.fromTimeText),
        toTime: this.textTimeToInt(this.form.toTimeText),
        symbolScope,
        mode: this.form.mode || 1,
        status: this.form.status,
        remark: this.form.remark
      }
      if (payload.fromTime === null || payload.toTime === null) {
        this.$message.warning('时间格式不正确')
        return
      }
      tradeHolidayAddOrUpdate(payload).then(res => {
        if (res.code === 0) {
          this.$message.success(res.result || '保存成功')
          this.dialogVisible = false
          this.fetchList()
          this.refreshLogsIfNeeded(payload.id)
        } else {
          this.$message.error(res.message || '保存失败')
          this.fetchList()
        }
      })
    },
    toggleStatus(row) {
      const targetStatus = row.status === 1 ? 2 : 1
      tradeHolidayStatus(row.id, targetStatus).then(res => {
        if (res.code === 0) {
          this.$message.success('状态更新成功')
          this.fetchList()
          this.refreshLogsIfNeeded(row.id)
        }
      })
    },
    handleRetry(row) {
      this.$confirm(`确定重试同步假期 ${row.description} 吗？`, '提示', { type: 'warning' }).then(() => {
        tradeHolidayRetryCreate({ id: row.id }).then(res => {
          if (res.code === 0) {
            this.$message.success(res.result || '重试成功')
            this.fetchList()
            this.refreshLogsIfNeeded(row.id)
          }
        })
      }).catch(() => {})
    },
    deleteRow(row) {
      this.$confirm(`确定删除假期配置 ${row.description} 吗？`, '提示', { type: 'warning' }).then(() => {
        tradeHolidayDeleteBatchIds(String(row.id)).then(res => {
          if (res.code === 0) {
            this.$message.success('删除成功')
            this.fetchList()
            this.refreshLogsIfNeeded(row.id)
          }
        })
      }).catch(() => {})
    },
    openLog(row) {
      this.currentLogHoliday = {
        id: row.id,
        description: row.description,
        holidayDate: row.holidayDate
      }
      this.logDialogTitle = `假期业务日志：${row.description || '-'}`
      this.logFilter = this.createEmptyLogFilter()
      this.openLogDialog()
    },
    openAllLogs() {
      this.currentLogHoliday = {
        id: null,
        description: '',
        holidayDate: ''
      }
      this.logDialogTitle = '假期业务日志'
      this.logFilter = this.createEmptyLogFilter()
      this.openLogDialog()
    },
    openLogDialog() {
      this.logPageNum = 1
      this.logPageSize = 10
      this.logDialogVisible = true
      this.fetchLogs()
    },
    fetchLogs() {
      this.logLoading = true
      const params = {
        pageNum: this.logPageNum,
        pageSize: this.logPageSize,
        ...this.logFilter
      }
      if (this.currentLogHoliday.id) {
        params.holidayId = this.currentLogHoliday.id
      }
      tradeHolidayLogList(params).then(res => {
        if (res.code === 0) {
          this.logTableData = (res.result && res.result.list) || []
          this.logTotal = (res.result && res.result.total) || 0
        }
      }).finally(() => {
        this.logLoading = false
      })
    },
    resetLogFilter() {
      this.logFilter = this.createEmptyLogFilter()
      this.logPageNum = 1
      this.fetchLogs()
    },
    refreshLogsIfNeeded(holidayId) {
      if (this.logDialogVisible && (!this.currentLogHoliday.id || this.currentLogHoliday.id === holidayId)) {
        this.fetchLogs()
      }
    },
    splitSymbolScope(value) {
      if (!value) return ['*']
      if (value === '*') return ['*']
      return value.split(',').map(item => item.trim()).filter(Boolean)
    },
    parseSymbolScopeToSelection(value) {
      const list = this.splitSymbolScope(value)
      if (!list.length) return ['*']
      return list
    },
    buildSymbolScope(values) {
      if (!Array.isArray(values) || values.length === 0) {
        return '*'
      }
      if (values.includes('*')) {
        return '*'
      }
      return Array.from(new Set(values)).join(',')
    },
    productScopeValue(item) {
      const category = item.bigCategoryName ? String(item.bigCategoryName).trim() : ''
      const code = item.productCode ? String(item.productCode).trim() : ''
      if (!category) return code
      return `${category}/${code}`
    },
    productScopeLabel(item, value) {
      const name = item.productName ? String(item.productName).trim() : ''
      return name ? `${value} - ${name}` : value
    },
    formatHolidayDate(value) {
      if (!value) {
        return '-'
      }
      return `${value} (${this.weekdayText(value)})`
    },
    weekdayText(value) {
      const date = new Date(`${value}T00:00:00`)
      if (Number.isNaN(date.getTime())) {
        return '-'
      }
      const map = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']
      return map[date.getDay()]
    },
    formatMinuteRange(fromTime, toTime) {
      return `${this.intTimeToText(fromTime)} - ${this.intTimeToText(toTime)}`
    },
    intTimeToText(value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }
      const str = String(value).padStart(4, '0')
      return `${str.slice(0, 2)}:${str.slice(2, 4)}`
    },
    textTimeToInt(value) {
      if (!value) return null
      const parts = value.split(':')
      if (parts.length !== 2) return null
      const hour = parts[0].padStart(2, '0')
      const minute = parts[1].padStart(2, '0')
      return Number(`${hour}${minute}`)
    },
    createStatusText(value) {
      const map = {
        0: '待同步',
        1: '同步中',
        2: '同步成功',
        3: '同步失败'
      }
      return map[value] || '-'
    },
    createStatusTag(value) {
      const map = {
        0: 'info',
        1: 'warning',
        2: 'success',
        3: 'danger'
      }
      return map[value] || 'info'
    },
    symbolTagClass(value) {
      if (value === '*') {
        return 'is-all'
      }
      if (String(value).startsWith('Index/')) {
        return 'is-index'
      }
      if (String(value).startsWith('Commodities/')) {
        return 'is-commodity'
      }
      return 'is-default'
    },
    actionTypeText(value) {
      const map = {
        1: '新增配置',
        2: '编辑配置',
        3: '启用假期',
        4: '禁用假期',
        5: '发起创建',
        6: '创建成功',
        7: '创建失败',
        8: '删除配置'
      }
      return map[value] || '-'
    },
    actionTypeTag(value) {
      const map = {
        1: 'primary',
        2: 'warning',
        3: 'success',
        4: 'info',
        5: 'warning',
        6: 'success',
        7: 'danger',
        8: 'danger'
      }
      return map[value] || 'info'
    }
  },
  computed: {
    productOptionsWithAll() {
      return [
        { value: '*', label: '全部产品 (*)' },
        ...this.productOptions
      ]
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 20px;
  background: #f5f7fb;
}

.info-bar {
  margin-bottom: 16px;
  padding: 12px 18px;
  background: #f7fbff;
  border: 1px solid #e8f1ff;
  border-radius: 10px;
  font-size: 12px;
  color: #6b7a99;
}

.panel-card {
  background: #fff;
  border: 1px solid #edf1f7;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(31, 35, 41, 0.04);
}

.search-card {
  margin-bottom: 16px;
  padding: 18px 20px 12px;
}

.table-header {
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.table-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2d3d;
}

.header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.header-actions :deep(.el-button) {
  min-width: 108px;
  border-radius: 8px;
}

.search-form {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.search-fields {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  flex: 1;
}

.field-item {
  margin-bottom: 10px;
}

::v-deep .search-card .search-form.el-form--label-top .el-form-item__label {
  float: none !important;
  display: inline-block !important;
  height: auto !important;
  padding: 0 0 6px !important;
  line-height: 18px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #5f6b7a !important;
}

::v-deep .search-card .search-form.el-form--label-top .el-form-item__content {
  line-height: normal !important;
}

.field-control {
  width: 190px;
}

.search-card :deep(.el-input__inner),
.search-card :deep(.el-select .el-input__inner),
.search-card :deep(.el-date-editor .el-input__inner) {
  height: 36px;
  line-height: 36px;
  border-radius: 8px;
}

.field-name .field-control {
  width: 180px;
}

.field-product .field-control {
  width: 220px;
}

.field-status .field-control {
  width: 120px;
}

.field-date {
  min-width: 340px;
}

.date-range-field {
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-control {
  width: 140px;
}

.search-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 10px;
}

.search-actions :deep(.el-button) {
  min-width: 74px;
  border-radius: 8px;
}

.table-card {
  padding: 20px;
}

.range-separator {
  color: #909399;
  font-size: 12px;
}

.time-range-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.symbol-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
}

.scope-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}

.scope-tag.is-index {
  background: #eaf2ff;
  color: #4f7ddb;
}

.scope-tag.is-commodity {
  background: #fff4d9;
  color: #c58a1c;
}

.scope-tag.is-all {
  background: #eaf8ee;
  color: #33a16d;
}

.scope-tag.is-default {
  background: #f1f3f7;
  color: #5f6b7a;
}

.holiday-table :deep(.el-table__header-wrapper th) {
  background: #fafbfc;
  color: #9aa4b5;
  font-weight: 600;
}

.holiday-table :deep(.el-table td),
.holiday-table :deep(.el-table th) {
  padding: 15px 0;
}

.holiday-table :deep(.el-table__body tr:hover > td) {
  background: #fcfdff;
}

.holiday-table :deep(.el-switch.is-checked .el-switch__core) {
  border-color: #0f9b8e;
  background-color: #0f9b8e;
}

.page-footer {
  margin-top: 18px;
  text-align: right;
}

.action-text {
  font-weight: 500;
  padding: 0 4px;
}

.action-text.is-edit {
  color: var(--brand-primary);
}

.action-text.is-log {
  color: var(--brand-primary);
}

.action-text.is-retry {
  color: #f59e0b;
}

.action-text.is-delete {
  color: #ef4444;
}

.holiday-form :deep(.el-form-item__label) {
  padding-bottom: 8px;
  line-height: 18px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.dialog-section + .dialog-section {
  margin-top: 18px;
}

.dialog-section {
  padding: 16px 18px 6px;
  background: #fafbfd;
  border: 1px solid #eef2f7;
  border-radius: 12px;
}

.section-title {
  margin-bottom: 14px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2d3d;
}

.time-control {
  width: 46%;
}

.selected-product-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.preview-tag {
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4);
}

.log-filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.log-title {
  min-width: 200px;
  font-weight: 600;
  color: #303133;
}

.danger-text {
  color: #f56c6c;
}

@media (max-width: 1400px) {
  .search-form {
    display: block;
  }

  .search-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 900px) {
  .holiday-dialog :deep(.el-dialog) {
    width: 92% !important;
  }

  .time-control {
    width: 44%;
  }

  .dialog-section {
    padding: 14px 14px 4px;
  }
}
</style>
